import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../utils/haptics';
import { sendAgentFeedback } from './agentApi';
import styles from './agentChat.module.css';

/** The reasons a customer's 👎 offers — the ids the server keeps (luna-feedback.rules.ts CUSTOMER_REASONS). */
export const CUSTOMER_FEEDBACK_REASONS = ['wrong_answer', 'didnt_help', 'not_understood', 'wrong_language', 'too_slow', 'other'];
const DEFAULTS = {
  wrong_answer: 'Wrong answer',
  didnt_help: "It didn't help me do it",
  not_understood: "Didn't understand me",
  wrong_language: 'Wrong language',
  too_slow: 'Too slow',
  other: 'Other',
};

function Thumb({ down }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={down ? { transform: 'scaleY(-1)' } : undefined}>
      <path d="M7 10v11H3V10h4z" />
      <path d="M7 10l4-7a2 2 0 0 1 2 2v4h6.2a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 18 20H7" />
    </svg>
  );
}

/**
 * THE THUMBS UNDER A REPLY (owner 10-24, as the portal's). 👍 and 👎 send at once; a 👎 asks what went wrong — a chip is
 * one tap that sends its reason, the text is optional. Tapping the lit thumb takes it back; a thumb the server did not
 * take does not stay lit. Whose conversation it is, the server works out from the login or the visitor key.
 */
export default function ReplyFeedback({ slug, turnId, conversationId, userMessage, reply, language, visitorKey }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(null);
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [text, setText] = useState('');
  const [thanks, setThanks] = useState(false);
  const base = { turnId, conversationId, userMessage, reply, language, ...(visitorKey ? { visitorKey } : {}) };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);
  useEffect(() => {
    if (!thanks) return undefined;
    const id = setTimeout(() => setThanks(false), 1800);
    return () => clearTimeout(id);
  }, [thanks]);

  const send = async (next, extra = {}) => {
    const before = rating;
    setRating(next === 'none' ? null : next);
    try {
      await sendAgentFeedback(slug, { ...base, rating: next, ...extra });
      return true;
    } catch (_) {
      setRating(before);
      haptic('warning');
      return false;
    }
  };
  const up = async () => { setOpen(false); await send(rating === 'up' ? 'none' : 'up'); };
  const down = async () => {
    if (rating === 'down') { setOpen(false); await send('none'); return; }
    setReasons([]); setText(''); setOpen(true); haptic('light');
    if (!(await send('down', { reasons: [] }))) setOpen(false);
  };
  const pick = async (id) => {
    const next = reasons.includes(id) ? reasons.filter((r) => r !== id) : [...reasons, id];
    setReasons(next);
    await send('down', { reasons: next, ...(text.trim() ? { text: text.trim() } : {}) });
  };
  const submit = async () => {
    if (await send('down', { reasons, ...(text.trim() ? { text: text.trim() } : {}) })) { haptic('success'); setOpen(false); setThanks(true); }
  };

  const good = t('agent.feedback.good', { defaultValue: 'Good answer' });
  const bad = t('agent.feedback.bad', { defaultValue: 'Not a good answer' });
  const title = t('agent.feedback.title', { defaultValue: "What wasn't right?" });
  const placeholder = t('agent.feedback.placeholder', { defaultValue: 'Tell us more (optional)' });
  return (
    <div className={styles.feedbackRow} data-testid="agent-feedback">
      <button type="button" className={`${styles.feedbackThumb} ${rating === 'up' ? styles.feedbackThumbOn : ''}`} onClick={up} aria-label={good} title={good} aria-pressed={rating === 'up'}><Thumb /></button>
      <button type="button" className={`${styles.feedbackThumb} ${rating === 'down' ? styles.feedbackThumbOn : ''}`} onClick={down} aria-label={bad} title={bad} aria-pressed={rating === 'down'}><Thumb down /></button>
      {thanks && <span className={styles.feedbackThanks} role="status">{t('agent.feedback.thanks', { defaultValue: 'Thanks, noted' })}</span>}
      {open && (
        <div className={styles.feedbackSheet} role="group" aria-label={title}>
          <div className={styles.feedbackTitle}>{title}</div>
          <div className={styles.feedbackChips}>
            {CUSTOMER_FEEDBACK_REASONS.map((id) => {
              const label = t(`agent.feedback.reasons.${id}`, { defaultValue: DEFAULTS[id] });
              return <button key={id} type="button" className={`${styles.feedbackChip} ${reasons.includes(id) ? styles.feedbackChipOn : ''}`} aria-pressed={reasons.includes(id)} onClick={() => pick(id)}>{label}</button>;
            })}
          </div>
          <textarea className={styles.feedbackText} rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
          <div className={styles.feedbackFoot}>
            <button type="button" className={styles.feedbackGhost} onClick={() => setOpen(false)}>{t('agent.feedback.close', { defaultValue: 'Close' })}</button>
            <button type="button" className={styles.feedbackSend} onClick={submit}>{t('agent.feedback.send', { defaultValue: 'Send' })}</button>
          </div>
        </div>
      )}
    </div>
  );
}
